import { createTheme, PaletteMode } from '@mui/material/styles';

/*
 * MUI theme mapped onto the DBBS Cerebellum Hub design system
 * (src/design-system/tokens). MUI derives hover/alpha shades from the palette at
 * runtime, so the palette carries literal hex values mirrored from
 * tokens/palette.css; component overrides reference the CSS custom properties
 * directly so they follow `data-theme` scopes (dark, instrument) without a
 * theme rebuild.
 */

const paper = {
  0: '#ffffff',
  50: '#fbfbf9',
  100: '#f4f4f1',
};

const ink = {
  900: '#0e1013',
  800: '#1b1e24',
  700: '#2b3038',
  500: '#626873',
  400: '#868c96',
  200: '#d2d5da',
  100: '#e7e9ec',
};

const graphite = {
  1000: '#08090b',
};

const cobalt = {
  900: '#062a80',
  800: '#0938a6',
  700: '#0b45cc',
  500: '#1a58f0',
  400: '#4d7ef5',
  300: '#8aa8f9',
};

const labelType = {
  fontFamily: 'var(--font-mono)',
  fontSize: 'var(--size-3xs)',
  fontWeight: 600,
  lineHeight: 1.2,
  letterSpacing: 'var(--tracking-widest)',
  textTransform: 'uppercase' as const,
};

export const createAppTheme = (mode: PaletteMode = 'light') => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: isDark
        ? { main: cobalt[500], light: cobalt[400], dark: cobalt[700], contrastText: paper[0] }
        : { main: cobalt[700], light: cobalt[500], dark: cobalt[800], contrastText: paper[0] },
      // The system has one action colour. Secondary is neutral ink so any
      // leftover `color="secondary"` renders quiet rather than as a second accent.
      secondary: isDark
        ? { main: '#a4abb6', light: '#dfe3e9', dark: '#5c636e', contrastText: graphite[1000] }
        : { main: ink[700], light: ink[500], dark: ink[900], contrastText: paper[0] },
      error: { main: isDark ? '#f4798c' : '#c0273a' },
      warning: { main: isDark ? '#ff8f1f' : '#a86a00' },
      success: { main: isDark ? '#4ad999' : '#127a4b' },
      info: { main: isDark ? cobalt[300] : cobalt[700] },
      background: {
        default: isDark ? '#171a20' : paper[50],
        paper: isDark ? '#1e222a' : paper[0],
      },
      text: {
        primary: isDark ? '#dfe3e9' : ink[800],
        secondary: isDark ? '#a4abb6' : ink[500],
        disabled: isDark ? '#5c636e' : ink[400],
      },
      divider: isDark ? 'rgba(255,255,255,.13)' : ink[200],
      action: {
        hover: isDark ? 'rgba(255,255,255,.06)' : 'rgba(14,16,19,.04)',
        selected: isDark ? '#16284f' : '#e6ecfe',
        disabledBackground: isDark ? 'rgba(255,255,255,.06)' : ink[100],
        disabled: isDark ? '#5c636e' : ink[400],
      },
    },
    typography: {
      fontFamily: 'var(--font-sans)',
      h1: { fontSize: 'var(--size-4xl)', fontWeight: 600, lineHeight: 1.2, letterSpacing: 'var(--tracking-tighter)' },
      h2: { fontSize: 'var(--size-2xl)', fontWeight: 600, lineHeight: 1.2, letterSpacing: 'var(--tracking-tight)' },
      h3: { fontSize: 'var(--size-xl)', fontWeight: 600, lineHeight: 1.2, letterSpacing: 'var(--tracking-tight)' },
      h4: { fontSize: 'var(--size-lg)', fontWeight: 600, lineHeight: 1.2, letterSpacing: 'var(--tracking-tight)' },
      h5: { fontSize: 'var(--size-md)', fontWeight: 600, lineHeight: 1.3 },
      h6: { fontSize: 'var(--size-sm)', fontWeight: 600, lineHeight: 1.3 },
      subtitle1: { fontSize: 'var(--size-md)', fontWeight: 500, lineHeight: 1.5 },
      subtitle2: { fontSize: 'var(--size-sm)', fontWeight: 500, lineHeight: 1.5 },
      body1: { fontSize: 'var(--size-md)', lineHeight: 1.65 },
      body2: { fontSize: 'var(--size-sm)', lineHeight: 1.5 },
      button: { fontSize: 'var(--size-sm)', fontWeight: 500, lineHeight: 1.2, textTransform: 'none' },
      caption: { fontSize: 'var(--size-2xs)', lineHeight: 1.4 },
      overline: labelType,
    },
    spacing: 8,
    shape: { borderRadius: 4 },
    shadows: [
      'none',
      'var(--shadow-sm)',
      'var(--shadow-md)',
      'var(--shadow-md)',
      'var(--shadow-lg)',
      ...Array(20).fill('var(--shadow-overlay)'),
    ] as any,
    transitions: {
      duration: { shortest: 80, shorter: 120, short: 180, standard: 180, complex: 240, enteringScreen: 180, leavingScreen: 120 },
      easing: {
        easeInOut: 'cubic-bezier(.2,0,.2,1)',
        easeOut: 'cubic-bezier(0,0,.2,1)',
        easeIn: 'cubic-bezier(.4,0,1,1)',
        sharp: 'cubic-bezier(.2,0,.2,1)',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: 'var(--surface-page)', color: 'var(--text-body)' },
        },
      },
      MuiButtonBase: {
        // Press feedback is a 0.5px nudge, never a ripple.
        defaultProps: { disableRipple: true },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-sm)',
            minHeight: 'var(--control-height-md)',
            paddingInline: 'var(--space-4)',
            transition: 'var(--transition-control)',
            '&:active': { transform: 'translateY(0.5px)' },
            // Unavailable actions stay visible; they are never faded out.
            '&.Mui-disabled': {
              backgroundColor: 'var(--action-disabled-bg)',
              color: 'var(--action-disabled-fg)',
              borderColor: 'transparent',
            },
          },
          sizeSmall: { minHeight: 'var(--control-height-sm)', paddingInline: 'var(--space-3)', fontSize: 'var(--size-2xs)' },
          sizeLarge: { minHeight: 'var(--control-height-lg)', paddingInline: 'var(--space-6)', fontSize: 'var(--size-md)' },
          containedPrimary: {
            backgroundColor: 'var(--action-primary-bg)',
            color: 'var(--action-primary-fg)',
            '&:hover': { backgroundColor: 'var(--action-primary-bg-hover)' },
            '&:active': { backgroundColor: 'var(--action-primary-bg-active)' },
          },
          outlined: {
            borderColor: 'var(--line-rule)',
            color: 'var(--text-strong)',
            '&:hover': { borderColor: 'var(--text-strong)', backgroundColor: 'var(--action-quiet-bg-hover)' },
          },
          text: {
            color: 'var(--text-body)',
            '&:hover': { backgroundColor: 'var(--action-quiet-bg-hover)' },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-sm)',
            color: 'inherit',
            transition: 'var(--transition-control)',
            '&:hover': { backgroundColor: 'var(--action-quiet-bg-hover)' },
            '&:active': { transform: 'translateY(0.5px)' },
          },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: 'var(--surface-raised)',
            color: 'var(--text-body)',
          },
          outlined: { borderColor: 'var(--line-rule)' },
          rounded: { borderRadius: 'var(--radius-md)' },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          // Cards carry no shadow: a 1px rule defines them.
          root: {
            backgroundColor: 'var(--surface-card)',
            border: '1px solid var(--line-rule)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'none',
          },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: 'var(--space-6)', '&:last-child': { paddingBottom: 'var(--space-6)' } } },
      },
      MuiCardActions: {
        styleOverrides: { root: { padding: 'var(--space-4) var(--space-6)', borderTop: '1px solid var(--line-hairline)' } },
      },
      MuiPopover: {
        styleOverrides: {
          paper: { border: '1px solid var(--line-rule)', boxShadow: 'var(--shadow-overlay)' },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { border: '1px solid var(--line-rule)', boxShadow: 'var(--shadow-overlay)' },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: 'var(--size-sm)',
            minHeight: 'var(--control-height-md)',
            '&.Mui-selected': { backgroundColor: 'var(--surface-accent)', color: 'var(--text-link)' },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { border: '1px solid var(--line-rule)', boxShadow: 'var(--shadow-overlay)' },
        },
      },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0, square: true },
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            borderBottom: '1px solid var(--line-hairline)',
            '&:before': { display: 'none' },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: { minHeight: 'var(--control-height-lg)', paddingInline: 'var(--space-4)' },
          content: { margin: 0 },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: { root: { padding: '0 var(--space-4) var(--space-4)' } },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 'var(--control-height-lg)', borderBottom: '1px solid var(--line-hairline)' },
          indicator: { height: 2, backgroundColor: 'var(--line-accent)' },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 'var(--control-height-lg)',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 'var(--size-sm)',
            color: 'var(--text-muted)',
            '&.Mui-selected': { color: 'var(--text-strong)' },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 'var(--radius-pill)', fontWeight: 500, height: 24, fontSize: 'var(--size-2xs)' },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: 'var(--graphite-900)',
            color: 'var(--paper-000)',
            border: '1px solid var(--alpha-paper-10)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--size-2xs)',
            fontWeight: 500,
          },
          arrow: { color: 'var(--graphite-900)' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--radius-sm)',
            backgroundColor: 'var(--surface-raised)',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--line-rule)' },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--text-muted)' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--focus-ring)', borderWidth: 1 },
          },
        },
      },
      MuiSelect: {
        styleOverrides: { select: { fontSize: 'var(--size-sm)' } },
      },
      MuiSlider: {
        styleOverrides: {
          root: { height: 2, color: 'var(--action-primary-bg)' },
          rail: { backgroundColor: 'var(--line-rule)', opacity: 1 },
          thumb: {
            width: 12,
            height: 12,
            boxShadow: 'none',
            '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 4px var(--alpha-ink-08)' },
          },
          valueLabel: {
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--size-2xs)',
            backgroundColor: 'var(--graphite-900)',
            borderRadius: 'var(--radius-sm)',
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: { '&.Mui-checked + .MuiSwitch-track': { opacity: 1 } },
          track: { backgroundColor: 'var(--line-rule)', opacity: 1 },
          thumb: { boxShadow: 'none' },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: 'var(--line-rule)' } },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { height: 2, borderRadius: 0, backgroundColor: 'var(--line-rule)' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: '1px solid var(--line-hairline)', fontSize: 'var(--size-sm)' },
          head: { ...labelType, color: 'var(--text-muted)' },
        },
      },
      MuiContainer: {
        defaultProps: { maxWidth: false },
        styleOverrides: {
          root: { maxWidth: 'var(--measure-page)', paddingInline: 'var(--gutter)' },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
      },
      MuiLink: {
        // base.css draws link underlines as a 1px bottom rule; MUI's own
        // text-decoration would double it.
        defaultProps: { underline: 'none' },
        styleOverrides: {
          root: {
            color: 'var(--text-link)',
            borderBottom: '1px solid currentColor',
            transition: 'var(--transition-control)',
            '&:hover': { color: 'var(--text-link-hover)' },
          },
        },
      },
    },
  });
};

export const theme = createAppTheme('light');
